import { useEffect, useState } from 'react';

import { Card, Empty } from './ui';
import { api } from '../lib/api';

interface ConfigRef {
  client: string;
  product: string;
  file: string;
  ownerMode: string;
  bytes: number;
}

interface ConfigDoc {
  client: string;
  product: string;
  file: string;
  path: string;
  content: string;
  /** Dotted paths whose values were replaced with a placeholder before sending. */
  redactedPaths: string[];
}

/**
 * Reads and edits product config files on a server.
 *
 * `/opt/<client>/config-<client>/<product>/<name>.json`, a separate tree from
 * the compose stacks.
 *
 * Secret-looking values never arrive here. They are replaced server-side with a
 * placeholder and put back on save, so a password can be left alone without
 * ever having been in the browser — type over the placeholder to change one.
 */
export function ConfigEditor({ serverId, canEdit }: { serverId: string; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<ConfigRef[] | null>(null);
  const [doc, setDoc] = useState<ConfigDoc | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    if (!open || files) return;
    api<ConfigRef[]>(`/servers/${serverId}/configs`)
      .then(setFiles)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not list config files'));
  }, [open, files, serverId]);

  const load = async (f: ConfigRef) => {
    setError(null);
    setSaved(null);
    setDoc(null);
    try {
      const d = await api<ConfigDoc>(
        `/servers/${serverId}/configs/${f.client}/${f.product}/${f.file}`,
      );
      setDoc(d);
      setDraft(d.content);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file');
    }
  };

  const save = async () => {
    if (!doc) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const r = await api<{ path: string; secretsPreserved: number }>(
        `/servers/${serverId}/configs/${doc.client}/${doc.product}/${doc.file}`,
        { method: 'PATCH', body: JSON.stringify({ content: draft }) },
      );
      setSaved(
        `Wrote ${r.path}` +
          (r.secretsPreserved > 0
            ? ` — ${r.secretsPreserved} redacted value(s) left as they were.`
            : '.'),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  // Parsed in the browser too, so a typo is caught before a round trip rather
  // than coming back as a server error.
  let localJsonError: string | null = null;
  if (doc) {
    try {
      JSON.parse(draft);
    } catch (e) {
      localJsonError = e instanceof Error ? e.message : 'Invalid JSON';
    }
  }
  const dirty = doc !== null && draft !== doc.content;

  if (!canEdit) return null;

  if (!open) {
    return (
      <Card title="Product configuration">
        <p className="muted small">
          JSON config files under <code>/opt/&lt;client&gt;/config-&lt;client&gt;/&lt;product&gt;/</code>.
        </p>
        <div className="cost-actions">
          <button type="button" className="ghost" onClick={() => setOpen(true)}>
            Show config files
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Product configuration"
      actions={
        <button type="button" className="ghost" onClick={() => setOpen(false)}>
          Close
        </button>
      }
    >
      {error && <p className="notice notice-bad" role="alert">{error}</p>}
      {files === null && !error && <p className="muted">Reading over SSH…</p>}
      {files?.length === 0 && <Empty>No config files found on this server.</Empty>}

      {files && files.length > 0 && (
        <div className="cred-tabs">
          {files.map((f) => {
            const id = `${f.client}/${f.product}/${f.file}`;
            const active = doc && `${doc.client}/${doc.product}/${doc.file}` === id;
            return (
              <button
                key={id}
                type="button"
                className={active ? 'tab tab-on' : 'tab'}
                onClick={() => void load(f)}
                title={`/opt/${f.client}/config-${f.client}/${f.product}/${f.file} · ${f.ownerMode} · ${f.bytes} bytes`}
              >
                {/* The client leads, because on a shared host the same product
                    has a config file per client and `hubiq/branding.json`
                    alone does not say whose. */}
                <span className="cfg-client">{f.client}</span>
                <span className="muted">/</span>
                {f.product}
                <span className="muted">/</span>
                <strong>{f.file}</strong>
              </button>
            );
          })}
        </div>
      )}

      {doc && (
        <>
          <p className="muted small">
            <code>{doc.path}</code>
          </p>

          {doc.redactedPaths.length > 0 && (
            // Named, not just counted. "Something is hidden" leaves the reader
            // unsure whether the thing they came to change is editable.
            <p className="notice">
              <strong>{doc.redactedPaths.length} secret value(s) are not shown:</strong>{' '}
              <code>{doc.redactedPaths.join(', ')}</code>. Leave the placeholder to keep the
              current value, or type over it to set a new one.
            </p>
          )}

          <textarea
            className="config-editor"
            spellCheck={false}
            rows={22}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />

          {localJsonError && (
            <p className="form-error" role="alert">
              Invalid JSON — {localJsonError}
            </p>
          )}

          <div className="cost-actions">
            <button
              type="button"
              disabled={busy || !dirty || localJsonError !== null}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="ghost"
              disabled={busy || !dirty}
              onClick={() => setDraft(doc.content)}
            >
              Revert
            </button>
            {dirty && !localJsonError && <span className="muted small">unsaved changes</span>}
          </div>

          <p className="muted small">
            Saving writes the file and keeps a timestamped backup beside it. The product still
            needs a <strong>Recreate</strong> — or whatever it does to reload config — to pick the
            change up.
          </p>

          {saved && <p className="notice">{saved}</p>}
        </>
      )}
    </Card>
  );
}
